// src/pages/CreateInvoicePage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reportsAPI, invoicesAPI, timeEntriesAPI, materialUsagesAPI } from '../services/api';

const CreateInvoicePage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [formData, setFormData] = useState({
    hourly_rate: 120, // Standardwert
    payment_type: 'invoice', // 'cash' oder 'invoice'
    notes: '',
    no_tax: false, // Option für MwSt entfernen
    travel_allowance: 0 // Neue Option für Anfahrtspauschale
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        console.log('Lade Daten für Rapport:', reportId);
        
        // Rapport laden
        const reportResponse = await reportsAPI.getById(reportId);
        const report = reportResponse.data.report;
        setReport(report);
        console.log('Rapport geladen:', report);
        
        // Zeiteinträge für das Projekt laden
        const timeResponse = await timeEntriesAPI.getByProject(report.project_id);
        const timeEntries = timeResponse.data.timeEntries || [];
        setTimeEntries(timeEntries);
        console.log('Zeiteinträge geladen:', timeEntries);
        
        // Materialeinträge für das Projekt laden
        const materialsResponse = await materialUsagesAPI.getByProject(report.project_id);
        const materials = materialsResponse.data.materialUsages || [];
        setMaterials(materials);
        console.log('Materialeinträge geladen:', materials);
        
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        setError('Die Daten konnten nicht geladen werden: ' + 
                 (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };

    if (reportId) {
      fetchData();
    }
  }, [reportId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateTotal = () => {
    // Materialkosten berechnen
    const materialCost = materials.reduce((sum, item) => {
      return sum + (item.quantity * item.price_per_unit);
    }, 0);
    console.log('Berechnete Materialkosten:', materialCost);

    // Arbeitskosten berechnen
    let laborHours = 0;
    let travelAllowanceFromEntries = 0; // Anfahrtspauschale aus Zeiteinträgen

    timeEntries.forEach(entry => {
      if (entry.start_time && entry.end_time) {
        const start = new Date(entry.start_time);
        const end = new Date(entry.end_time);
        
        // Berechne die Differenz direkt - da beide Daten entweder mit oder ohne Z sind, 
        // spielt die Zeitzone für die Differenz keine Rolle
        const hours = (end - start) / (1000 * 60 * 60);
        
        // Anzahl der Mitarbeiter berücksichtigen (Standardwert 1, falls nicht angegeben)
        const employeeCount = entry.employee_count ? parseInt(entry.employee_count) : 1;
        const totalHoursForEntry = hours * employeeCount;
        
        laborHours += totalHoursForEntry;
        console.log(`Zeiteintrag: ${hours} Stunden × ${employeeCount} Mitarbeiter = ${totalHoursForEntry} Arbeitsstunden`);
        
        // Anfahrtspauschale aus Zeiteinträgen berechnen
        if (entry.travel_allowance && parseFloat(entry.travel_allowance) > 0) {
          travelAllowanceFromEntries += parseFloat(entry.travel_allowance);
        }
      }
    });
    
    // Manuelle Anfahrtspauschale überschreibt die aus Zeiteinträgen
    const travelAllowanceTotal = formData.travel_allowance && formData.travel_allowance.toString().trim() !== '' && parseFloat(formData.travel_allowance) > 0 
      ? parseFloat(formData.travel_allowance) 
      : travelAllowanceFromEntries;
    
    console.log('Manuell eingegebene Anfahrtspauschale:', formData.travel_allowance);
    console.log('Verwendete Anfahrtspauschale:', travelAllowanceTotal);
    
    const laborCost = laborHours * formData.hourly_rate;
    console.log('Berechnete Arbeitsstunden (inkl. Mitarbeiter):', laborHours);
    console.log('Berechnete Arbeitskosten:', laborCost);
    console.log('Anfahrtspauschale Total:', travelAllowanceTotal);

    // Summen und MwSt. berechnen
    const subtotal = materialCost + laborCost + travelAllowanceTotal;
    const taxRate = formData.no_tax ? 0 : 0.077; // 7.7% MwSt. oder 0% wenn no_tax aktiv
    const tax = subtotal * taxRate;
    const total = subtotal + tax;

    return {
      materialCost,
      laborHours,
      laborCost,
      travelAllowanceTotal,
      subtotal,
      tax,
      total
    };
  };

  const formatMySQLDate = (date) => {
    return date.toISOString().slice(0, 19).replace('T', ' ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const calculation = calculateTotal();
      
      // Rechnungsdaten vorbereiten
      const currentDate = new Date();
      const dueDate = new Date(currentDate);
      dueDate.setDate(dueDate.getDate() + 20); // 20 Tage Zahlungsfrist

      // Werte für die Datenbank vorbereiten
      const invoiceData = {
        report_id: parseInt(reportId, 10),
        invoice_date: formatMySQLDate(currentDate),
        due_date: formatMySQLDate(dueDate),
        hourly_rate: parseFloat(formData.hourly_rate) || 0,
        payment_type: formData.payment_type,
        notes: formData.notes || "",
        material_cost: parseFloat(calculation.materialCost.toFixed(2)) || 0,
        labor_hours: parseFloat(calculation.laborHours.toFixed(2)) || 0,
        labor_cost: parseFloat(calculation.laborCost.toFixed(2)) || 0,
        travel_allowance: parseFloat(calculation.travelAllowanceTotal.toFixed(2)) || 0,
        subtotal: parseFloat(calculation.subtotal.toFixed(2)) || 0,
        tax: parseFloat(calculation.tax.toFixed(2)) || 0,
        total: parseFloat(calculation.total.toFixed(2)) || 0,
        status: formData.payment_type === 'cash' ? 'paid' : 'pending',
        no_tax: formData.no_tax // Option für MwSt entfernen weitergeben
      };
      
      console.log('Erstelle Rechnung mit Daten:', invoiceData);
      
      // Rechnung erstellen
      const response = await invoicesAPI.create(invoiceData);
      console.log('Rechnung erstellt:', response.data);
      
      alert(`Rechnung erfolgreich erstellt!`);
      navigate(`/rechnungen/${response.data.invoice.id}`);
    } catch (err) {
      console.error('Fehler beim Erstellen der Rechnung:', err);
      console.error('Response Status:', err.response?.status);
      console.error('Response Data:', err.response?.data);
      console.error('Error Message:', err.message);
      
      setError('Fehler beim Erstellen der Rechnung: ' + 
               (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return <div className="text-center py-10">Lade Rapport-Daten...</div>;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
        <button 
          onClick={() => navigate('/rapporte')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
        >
          Zurück zur Rapportliste
        </button>
      </div>
    );
  }

  if (!report) {
    return <div className="text-center py-10">Rapport nicht gefunden</div>;
  }

  const calculation = calculateTotal();

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">Rechnung erstellen</h1>
      
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-medium mb-4">Rapport-Informationen</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-600">Kunde</p>
            <p className="font-medium">{report.company_name || `${report.first_name} ${report.last_name}`}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Projekt</p>
            <p className="font-medium">{report.project_title}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Rapport-Nummer</p>
            <p className="font-medium">{report.id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Datum</p>
            <p className="font-medium">{new Date(report.created_at).toLocaleDateString('de-DE')}</p>
          </div>
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-medium mb-4">Material und Zeiterfassung</h2>
        
        {materials.length > 0 ? (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Verwendete Materialien</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">Material</th>
                    <th className="px-4 py-2 text-left">Menge</th>
                    <th className="px-4 py-2 text-left">Einheit</th>
                    <th className="px-4 py-2 text-left">Preis</th>
                    <th className="px-4 py-2 text-left">Summe</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-2">{item.material_name}</td>
                      <td className="px-4 py-2">{item.quantity}</td>
                      <td className="px-4 py-2">{item.unit}</td>
                      <td className="px-4 py-2">CHF {parseFloat(item.price_per_unit).toFixed(2)}</td>
                      <td className="px-4 py-2">CHF {(item.quantity * item.price_per_unit).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="mb-6 text-gray-500">Keine Materialien erfasst</p>
        )}
        
        {timeEntries.length > 0 ? (
          <div>
            <h3 className="text-lg font-medium mb-2">Erfasste Arbeitszeit</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left">Datum</th>
                    <th className="px-4 py-2 text-left">Von</th>
                    <th className="px-4 py-2 text-left">Bis</th>
                    <th className="px-4 py-2 text-left">Stunden</th>
                    <th className="px-4 py-2 text-left">Mitarbeiter</th>
                    <th className="px-4 py-2 text-left">Gesamtstunden</th>
                    <th className="px-4 py-2 text-left">Anfahrt</th>
                  </tr>
                </thead>
                <tbody>
                  {timeEntries.map((entry, index) => {
                    const start = new Date(entry.start_time);
                    const end = entry.end_time ? new Date(entry.end_time) : null;
                    
                    // Berechnung der Stunden direkt aus den Zeitwerten ohne Zeitzonenverschiebung
                    let hours = '-';
                    if (end) {
                      // Berechne die Differenz in Millisekunden und dann in Stunden umwandeln
                      // Wenn beide Daten das 'Z' haben oder nicht haben, spielt die Zeitzone keine Rolle für die Differenz
                      const diffInMs = end - start;
                      hours = (diffInMs / (1000 * 60 * 60)).toFixed(2);
                    }
                    
                    const employeeCount = entry.employee_count ? parseInt(entry.employee_count) : 1;
                    const totalHours = end ? (parseFloat(hours) * employeeCount).toFixed(2) : '-';
                    
                    return (
                      <tr key={index} className="border-t">
                        <td className="px-4 py-2">{start.toLocaleDateString('de-DE')}</td>
                        <td className="px-4 py-2">{start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="px-4 py-2">{end ? end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                        <td className="px-4 py-2">{hours}</td>
                        <td className="px-4 py-2">{employeeCount}</td>
                        <td className="px-4 py-2">{totalHours}</td>
                        <td className="px-4 py-2">{entry.travel_allowance ? `CHF ${parseFloat(entry.travel_allowance).toFixed(2)}` : 'Nein'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">Keine Arbeitszeit erfasst</p>
        )}
      </div>
      
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-medium mb-4">Rechnungsdetails</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Stundenansatz (CHF)</label>
            <input
              type="number"
              name="hourly_rate"
              value={formData.hourly_rate}
              onChange={handleChange}
              min="1"
              step="1"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Anfahrtspauschale (CHF)</label>
            <input
              type="number"
              name="travel_allowance"
              value={formData.travel_allowance}
              onChange={handleChange}
              min="0"
              step="0.05"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              Wenn auf 0 belassen, wird die aus Zeiteinträgen berechnete Pauschale verwendet (CHF {calculation.travelAllowanceTotal.toFixed(2)})
            </p>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Zahlungsart</label>
            <div className="flex gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="payment_type"
                  value="cash"
                  checked={formData.payment_type === 'cash'}
                  onChange={handleChange}
                  className="form-radio h-4 w-4 text-primary-600"
                />
                <span className="ml-2">Bar bezahlt</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="payment_type"
                  value="invoice"
                  checked={formData.payment_type === 'invoice'}
                  onChange={handleChange}
                  className="form-radio h-4 w-4 text-primary-600"
                />
                <span className="ml-2">Rechnung (zahlbar innert 20 Tagen)</span>
              </label>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="no_tax"
                checked={formData.no_tax}
                onChange={handleChange}
                className="form-checkbox h-4 w-4 text-primary-600"
              />
              <span className="ml-2">Keine MwSt. berechnen</span>
            </label>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Bemerkungen (optional)</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            ></textarea>
          </div>
          
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h3 className="text-lg font-medium mb-4">Zusammenfassung</h3>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span>Material:</span>
                <span>CHF {calculation.materialCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Arbeitsstunden:</span>
                <span>{calculation.laborHours.toFixed(2)} h</span>
              </div>
              <div className="flex justify-between">
                <span>Stundenansatz:</span>
                <span>CHF {parseFloat(formData.hourly_rate).toFixed(2)} / h</span>
              </div>
              <div className="flex justify-between">
                <span>Arbeitskosten:</span>
                <span>CHF {calculation.laborCost.toFixed(2)}</span>
              </div>
              {calculation.travelAllowanceTotal > 0 && (
                <div className="flex justify-between">
                  <span>Anfahrtspauschale:</span>
                  <span>CHF {calculation.travelAllowanceTotal.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium">
                <span>Zwischensumme:</span>
                <span>CHF {calculation.subtotal.toFixed(2)}</span>
              </div>
              {!formData.no_tax && (
                <div className="flex justify-between">
                  <span>MwSt. (7.7%):</span>
                  <span>CHF {calculation.tax.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold">
                <span>Gesamtbetrag:</span>
                <span>CHF {calculation.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/rapporte')}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded"
            >
              Rechnung erstellen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInvoicePage;